import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Login from './pages/Login';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import FranchiseDashboard from './pages/FranchiseDashboard';
import GenerateOrder from './pages/GenerateOrder';
import OrderDetail from './pages/OrderDetail';
import LedgerManagement from './pages/LedgerManagement';
import RawMaterialOrder from './pages/RawMaterialOrder';
import FOSLandingPage from './pages/FOSLandingPage';
import NetworkStatus from './components/NetworkStatus';
import UpdateNotification from './components/UpdateNotification';
import { Toaster } from './components/ui/sonner';
import StorageService from './services/storageService';
import syncService from './services/syncService';
import './App.css';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load user from localStorage and initialize IndexedDB on app start
  useEffect(() => {
    const initializeApp = async () => {
      try {
        // Load user from localStorage FIRST (critical for page reload)
        const savedUser = localStorage.getItem('user');
        if (savedUser) {
          try {
            const parsedUser = JSON.parse(savedUser);
            setUser(parsedUser);
            console.log('[App] User loaded from localStorage:', parsedUser.role);
            
            // Start auto-sync for franchise users only
            if (parsedUser.role === 'franchise') {
              syncService.startAutoSync();
              console.log('[App] Auto-sync started for franchise user');
            }
          } catch (parseError) {
            console.error('[App] Error parsing saved user:', parseError);
            localStorage.removeItem('user');
          }
        }

        // Initialize IndexedDB (non-critical, don't fail if it errors)
        try {
          await StorageService.init();
          console.log('[App] IndexedDB initialized');
        } catch (dbError) {
          console.warn('[App] IndexedDB initialization failed (app will still work):', dbError);
        }
      } catch (error) {
        console.error('[App] Error initializing app:', error);
      } finally {
        setLoading(false);
      }
    };

    initializeApp();

    // Cleanup on unmount
    return () => {
      syncService.stopAutoSync();
    };
  }, []);

  // Save user to localStorage whenever it changes
  useEffect(() => {
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
    } else {
      localStorage.removeItem('user');
    }
  }, [user]);

  // Show loading screen while checking for saved user
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-secondary">Loading...</p>
        </div>
      </div>
    );
  }

  // Show loading screen while checking for saved user
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-lg text-secondary">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="App min-h-screen bg-background">
      <UpdateNotification />
      {user?.role === 'franchise' && <NetworkStatus />}
      <BrowserRouter>
        <Routes>
          {/* Public FOS Landing Page */}
          <Route path="/fos" element={<FOSLandingPage />} />
          
          <Route path="/" element={user ? <Navigate to={user.role === 'admin' ? '/admin' : '/franchise'} /> : <Login setUser={setUser} />} />
          <Route path="/login" element={<Login setUser={setUser} />} />
          <Route path="/admin-login" element={user ? <Navigate to={user.role === 'admin' ? '/admin' : '/franchise'} /> : <AdminLogin setUser={setUser} />} />
          <Route path="/admin/*" element={user?.role === 'admin' ? <AdminDashboard user={user} setUser={setUser} /> : <Navigate to="/" />} />
          <Route path="/franchise/*" element={user?.role === 'franchise' ? <FranchiseDashboard user={user} setUser={setUser} /> : <Navigate to="/" />} />
          <Route path="/generate-order" element={user?.role === 'franchise' ? <GenerateOrder user={user} /> : <Navigate to="/" />} />
          <Route path="/ledger-management" element={user?.role === 'franchise' ? <LedgerManagement user={user} /> : <Navigate to="/" />} />
          <Route path="/raw-material-order" element={user?.role === 'franchise' ? <RawMaterialOrder user={user} /> : <Navigate to="/" />} />
          <Route path="/order/:orderId" element={user ? <OrderDetail user={user} /> : <Navigate to="/" />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;